/**
 * The film archive's browser side: save the war's replay log as a `.friction-replay` download,
 * and read a record the player picks — then screen it in the replay viewer. The file format and
 * its validation (size caps, strict MessagePack, schemas, versions) live in @friction/protocol
 * (replay-file.ts); nothing here trusts a file more than that does.
 */
import {
  decodeReplayFile,
  encodeReplayFile,
  LIMITS,
  REPLAY_FILE_EXTENSION,
  type ReplayFile,
  ReplayFileError,
  type ReplayFileErrorCode,
  replayFileHeader,
} from '@friction/protocol';
import { createStore, useStore } from 'zustand';
import { liveGame, replayStore } from './store';

/** A file name from the theatre and the date: "friction-the-varnian-marches-1924-05-01.friction-replay". */
export function replayFileName(theatre: string, savedAt: Date): string {
  const slug = theatre
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  const day = savedAt.toISOString().slice(0, 10);
  return `friction-${slug || 'war'}-${day}${REPLAY_FILE_EXTENSION}`;
}

/**
 * Save the finished war's record (the log the host sent) as a download. The header takes the
 * theatre, nations and victors from the live game's final view. Resolves false when there is
 * nothing to save or encoding failed.
 */
export async function saveRecord(): Promise<boolean> {
  const log = replayStore.getState().log;
  const game = liveGame();
  const view = game.view;
  if (!log || !game.map || !view) return false;
  try {
    const now = new Date();
    const header = replayFileHeader(log, game.map.name, view.factions, view.winners, now);
    const bytes = await encodeReplayFile({ header, log });
    download(new Blob([bytes], { type: 'application/octet-stream' }), replayFileName(game.map.name, now));
    return true;
  } catch (err) {
    console.error('[archive] could not save the record', err);
    return false;
  }
}

function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser time to start the download before the URL goes.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ---- the archive (loading) ----------------------------------------------------------------------

export interface ArchiveState {
  /** The record on the projector (validated), or null. */
  file: ReplayFile | null;
  /** File name it came from. */
  name: string | null;
  loading: boolean;
  /** Why the last file was refused. */
  error: ReplayFileErrorCode | 'read' | null;
}

/** Kept outside the screen so the loaded record survives screening it (the screen unmounts). */
export const archiveStore = createStore<ArchiveState>()(() => ({
  file: null,
  name: null,
  loading: false,
  error: null,
}));

export function useArchive<T>(selector: (s: ArchiveState) => T): T {
  return useStore(archiveStore, selector);
}

/** Read, validate and put a record on the projector (or say why not). */
export async function loadRecord(file: Blob & { name?: string }): Promise<void> {
  archiveStore.setState({ loading: true, error: null });
  try {
    // Refuse before reading a huge file into memory.
    if (file.size > LIMITS.replayFileBytes)
      throw new ReplayFileError('tooLarge', `file of ${file.size} bytes`);
    let bytes: Uint8Array;
    try {
      bytes = new Uint8Array(await file.arrayBuffer());
    } catch {
      archiveStore.setState({ loading: false, error: 'read' });
      return;
    }
    const record = await decodeReplayFile(bytes);
    archiveStore.setState({ file: record, name: file.name ?? null, loading: false, error: null });
  } catch (err) {
    const code = err instanceof ReplayFileError ? err.code : 'invalid';
    if (!(err instanceof ReplayFileError)) console.error('[archive] unexpected error reading a record', err);
    archiveStore.setState({ loading: false, error: code });
  }
}
